// Browser-local learning stats, persisted in localStorage.
// Data shape: { "YYYY-MM-DD": <solvedCount>, ... }

const STORAGE_KEY = 'dari-stats-v1';

// Local-time date key (not UTC) so "today" matches the user's calendar.
function dayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private-mode errors
  }
}

// Record one solved puzzle for today. Returns the updated stats summary.
export function recordSolved() {
  const data = load();
  const key = dayKey();
  data[key] = (data[key] || 0) + 1;
  save(data);
  return getStats();
}

// Counts for the last `days` days, oldest first, each as { key, label, count }.
export function lastDays(days = 7) {
  const data = load();
  const result = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = dayKey(d);
    result.push({
      key,
      label: d.toLocaleDateString(undefined, { weekday: 'short' }),
      count: data[key] || 0,
    });
  }
  return result;
}

// Consecutive days with at least one solve, ending today.
// Today counting as 0 doesn't break the streak (the day isn't over yet).
export function currentStreak() {
  const data = load();
  let streak = 0;
  const cursor = new Date();
  if (!(data[dayKey(cursor)] > 0)) {
    // No activity today yet — start counting from yesterday.
    cursor.setDate(cursor.getDate() - 1);
  }
  while (data[dayKey(cursor)] > 0) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

// Most recent local date key with at least one solve, or null if never.
export function lastSolvedDay() {
  const data = load();
  const keys = Object.keys(data).filter((k) => data[k] > 0).sort();
  return keys.length ? keys[keys.length - 1] : null;
}

export function getStats() {
  const data = load();
  return {
    today: data[dayKey()] || 0,
    streak: currentStreak(),
    days: lastDays(7),
    total: Object.values(data).reduce((sum, n) => sum + (n || 0), 0),
  };
}
